import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { splitDeadlines, wordsOf } from '../todos/todoFormat';
import { ASSISTANT_BUBBLE, LINE_BUBBLE } from '../chat/bubbles';
import { RefreshIcon } from '../chat/icons';
import {
  api, type CardText, type MorningCall, type MorningDeadline, type MorningDismissKind, type MorningItemRef,
  type MorningMailItem, type MorningPromise, type MorningRef, type MorningText, type MorningTodo, type MyMorning,
} from '../../lib/api';
import {
  CallCard, DeadlineCard, GroupCard, LINK, MailCard, NOT_SAVED, TextCard, PromiseCard, TodoCard, Unreadable, draftPromiseWords, draftReplyWords,
  type GroupItem, type Hide,
} from './MorningRows';
import SuggestedForYou from './SuggestedForYou';
import { cardKeyOf, useCardText } from './useCardText';

/**
 * MORNING2 (Task 9) - the brief at the top of the morning chat, in the Claude Duet style he picked (2026-10-28, option 1:
 * no AI - every card is written from the item's own fields). It is the FIRST block of the chat's message list (the panel's
 * `lead`), so the page is one scroll: the greeting, Needs you, Can wait (folded), the Deal Radar line, then the
 * conversation and the chat box.
 *  - The greeting is a chat bubble: the server's opening lines - the counts, then one "Couldn't read X - try again." per
 *    section it could not read - with Try again under them. ⛔ An unreadable section is never "nothing waiting".
 *  - Each item of `chat.groups` is drawn from its row in the sections (a ref with no row - hidden, or gone - is skipped).
 *  - Can wait is behind "Show the N" (N = what is listed + what the lists did not show; "N or more" when Gmail had more than
 *    was read); open, it shows the same cards, then where the rest are ("and 3 more texts").
 *  - Start sends a chat turn through the panel's own send (`onDraft`); ⛔ every Start is off while an answer is arriving
 *    (`answering`) - the panel would drop a second send.
 *  - Hide takes the card away at once and calls today's dismiss; a refusal puts it back with the server's words. A new
 *    read clears every hide but those still in flight, as today's page clears its marks: an item the server lists again
 *    (a newer message after the dismiss) comes back.
 *  - Deadlines more than 30 days overdue stay behind one tap that names how many (his word, 2026-10-26).
 *  - MORNING2-AI (option 2): the page draws this plain list FIRST and never waits on the AI. It then asks the server for the
 *    checked AI title and reason of the cards it SHOWS (Needs you at once; Can wait and the older deadlines once he opens
 *    them) and each card swaps its heading and line in as the text arrives, with a Why? that shows the source lines. A
 *    card with no text, and every card when the request fails, keeps its plain line - no error, no spinner.
 *    When the server says the AI is paused, one line under the greeting says so.
 *  - MORNING2-AI Task 8 (design 4.2): `chat.contact_groups` names the items that share one GoHighLevel contact. Each group is
 *    ONE card (one AI text - its `cards` entry is the whole group) listing its items with their own buttons, in Needs you
 *    when any of its items is there, else in Can wait; an item inside a group is never ALSO a card of its own. Hide on the
 *    group dismisses each item in it that phase 1 can dismiss (email, text, missed call) - a task or deadline cannot be,
 *    so it simply stays and shows alone. A group that has lost items (hidden, or gone from the lists) to fewer than two is
 *    no group: its remaining item shows as an ordinary card.
 */

/** The words when the AI is paused (design 6): the day's limit, or a cost that could not be recorded. */
const PAUSED_LINE = 'AI titles paused until tomorrow - daily limit reached.';
const UNRECORDED_LINE = "AI titles paused - their cost couldn't be recorded.";

type ChatOn = Extract<NonNullable<MyMorning['chat']>, { on: true }>;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/** The record without one key. */
const without = <V,>(record: Record<string, V>, key: string): Record<string, V> =>
  Object.fromEntries(Object.entries(record).filter(([k]) => k !== key));
const refKey = (ref: MorningRef): string => `${ref.kind}:${ref.id}`;
/** One card on screen: one item, or the several items one contact has waiting. */
type Unit = MorningRef[];
/** The kinds "Not needed" can mark (`api.morning.dismiss`). A task or deadline is not among them. */
const isDismissible = (ref: MorningRef): ref is MorningRef & { kind: MorningDismissKind } =>
  ref.kind === 'mail' || ref.kind === 'text' || ref.kind === 'call' || ref.kind === 'promise';

/**
 * R59: the items (and the groups that join them) a read lists, in any order - what the cards are made of. Two reads with the same
 * signature have the same card keys. Hidden items are still in it, so a Hide never reads as a new set.
 */
function cardSignature(data: MyMorning): string {
  const chat = data.chat as { groups?: { needs_you?: MorningRef[]; can_wait?: MorningRef[] }; contact_groups?: MorningRef[][] } | undefined;
  const items = [...(chat?.groups?.needs_you ?? []), ...(chat?.groups?.can_wait ?? [])].map(refKey).sort();
  const groups = (chat?.contact_groups ?? []).map((g) => cardKeyOf(g)).sort();
  return `${items.join(',')}|${groups.join(',')}`;
}

export default function MorningBrief({ data, onDraft, onChanged, answering = false, reading = false, autoRead = false }: {
  /** A linked, switched-on morning (`chat.on`). */
  data: MyMorning;
  /** A Start: the words of the turn, and the item his tap started it from. */
  onDraft: (text: string, item: MorningItemRef) => void;
  /** Read the list again (Try again). */
  onChanged: () => void;
  /** An answer is arriving in the chat: every Start is off. */
  answering?: boolean;
  /** A read is under way (the Try again buttons say so). */
  reading?: boolean;
  /**
   * R59: `data` came from the page's automatic re-read (a section was unreadable), not from a Refresh, Try again or a change he
   * made. Such a read re-asks the AI text only when its cards changed; the cards already answered keep their text.
   */
  autoRead?: boolean;
}) {
  // Hide: `kind:id` -> gone at once; the words when the server refused it (and it came back).
  const [hidden, setHidden] = useState<Record<string, true>>({});
  const [hideErrors, setHideErrors] = useState<Record<string, string>>({});
  /** The hides the server has not answered yet - the only ones a new read keeps. */
  const inFlight = useRef(new Set<string>());
  const [showCanWait, setShowCanWait] = useState(false);
  const [showOld, setShowOld] = useState(false);
  // Bumped on every new read: the to-do cards remount, so "Marked done. Undo" lasts until the next read and no longer.
  const [seen, setSeen] = useState(data);
  const [epoch, setEpoch] = useState(0);
  // R59: the read the AI text is asked for. Every read but an automatic one with the same cards is a new one ("never stale text"
  // after a Refresh); the automatic minute re-read would otherwise ask every card again each minute while a section is down.
  const [textRead, setTextRead] = useState(0);
  // Every read but the automatic minute re-read (a Refresh, Try again, a change he made): "Suggested for you" reads again too.
  const [suggestRead, setSuggestRead] = useState(0);
  if (seen !== data) {
    if (!autoRead || cardSignature(seen) !== cardSignature(data)) setTextRead((n) => n + 1);
    if (!autoRead) setSuggestRead((n) => n + 1);
    setSeen(data);
    setEpoch((n) => n + 1);
    // ⭐ As today's page clears `gone` on every good read: what the server lists now is what shows, but a hide it has not
    //   answered yet stays hidden (its answer is on the way).
    setHidden((h) => Object.fromEntries(Object.keys(h).filter((k) => inFlight.current.has(k)).map((k) => [k, true as const])));
    setHideErrors({});
  }

  const chat = data.chat as ChatOn;
  const mail = data.mail.status === 'ok' ? data.mail : null;
  const byId = <T,>(items: T[], idOf: (x: T) => string) => new Map(items.map((x) => [idOf(x), x]));
  const mailById = byId<MorningMailItem>(mail?.items ?? [], (t) => t.thread_id);
  const textById = byId<MorningText>(data.texts.status === 'ok' ? data.texts.items : [], (t) => t.conversation_id);
  const callById = byId<MorningCall>(data.calls.status === 'ok' ? data.calls.items : [], (c) => c.conversation_id);
  const todoById = byId<MorningTodo>(data.todos.status === 'ok' ? data.todos.items : [], (t) => t.ghl_task_id);
  const deadlineById = byId<MorningDeadline>(data.deadlines.status === 'ok' ? data.deadlines.items : [], (d) => d.deadline_id);
  // Task 10: promises ride on the switched-on answer only (absent = none, an older server).
  const promiseById = byId<MorningPromise>(chat.promises ?? [], (p) => p.message_id);

  const hide = (kind: MorningDismissKind, id: string): Hide => {
    const key = `${kind}:${id}`;
    return {
      error: hideErrors[key] ?? null,
      onHide: () => {
        setHidden((h) => ({ ...h, [key]: true }));
        setHideErrors((e) => without(e, key));
        inFlight.current.add(key);
        api.morning.dismiss(kind, id).then(() => { inFlight.current.delete(key); }, (e: unknown) => {
          inFlight.current.delete(key);
          // ⛔ Refused: the card comes back, with the server's words.
          setHidden((h) => without(h, key));
          setHideErrors((x) => ({ ...x, [key]: wordsOf(e, NOT_SAVED) }));
        });
      },
    };
  };

  /** Does this item have a card on screen: a row to draw it from, and not hidden. */
  const hasCard = (ref: MorningRef): boolean => {
    if (hidden[`${ref.kind}:${ref.id}`]) return false;
    switch (ref.kind) {
      case 'mail': return mailById.has(ref.id);
      case 'text': return textById.has(ref.id);
      case 'call': return callById.has(ref.id);
      case 'todo': return todoById.has(ref.id);
      case 'deadline': return deadlineById.has(ref.id);
      case 'promise': return promiseById.has(ref.id);
      default: return false;
    }
  };

  /** One item's card, or null when it has no row to draw from (hidden, or not in the sections any more). */
  const cardFor = (ref: MorningRef): ReactNode => {
    const key = `${ref.kind}:${ref.id}`;
    if (hidden[key]) return null;
    const aiText = aiFor([ref]);
    switch (ref.kind) {
      case 'mail': {
        const t = mailById.get(ref.id);
        return t ? (
          <li key={key}>
            <MailCard t={t} answering={answering} hide={hide('mail', t.thread_id)} ai={aiText}
              onStart={() => onDraft(draftReplyWords(t), { kind: 'mail', id: t.thread_id })} />
          </li>
        ) : null;
      }
      case 'text': {
        const t = textById.get(ref.id);
        return t ? <li key={key}><TextCard t={t} hide={hide('text', t.conversation_id)} ai={aiText} /></li> : null;
      }
      case 'call': {
        const c = callById.get(ref.id);
        return c ? <li key={key}><CallCard c={c} hide={hide('call', c.conversation_id)} ai={aiText} /></li> : null;
      }
      case 'todo': {
        const t = todoById.get(ref.id);
        if (!t) return null;
        // The waiting email from its contact - only while it is on his list.
        const reply = t.reply_thread_id ? mailById.get(t.reply_thread_id) ?? null : null;
        return (
          <TodoCard key={`${epoch}:${key}`} todo={t} mail={reply} answering={answering} ai={aiText}
            onStart={() => reply && onDraft(draftReplyWords(reply), { kind: 'mail', id: reply.thread_id, todo_task_id: t.ghl_task_id })} />
        );
      }
      case 'deadline': {
        const d = deadlineById.get(ref.id);
        return d ? <li key={key}><DeadlineCard d={d} ai={aiText} /></li> : null;
      }
      case 'promise': {
        const p = promiseById.get(ref.id);
        // Draft reply starts the thread's reply exactly as a mail card's Start does (the same item the panel sends).
        return p ? (
          <li key={key}>
            <PromiseCard p={p} answering={answering} hide={hide('promise', p.message_id)} ai={aiText}
              onStart={() => onDraft(draftPromiseWords(p), { kind: 'mail', id: p.thread_id })} />
          </li>
        ) : null;
      }
      default:
        return null;
    }
  };

  /** One group's card: its items drawn from their rows, each with its own buttons, and one Hide for them all. */
  const groupCardFor = (refs: Unit): ReactNode => {
    const items = refs.flatMap((ref): GroupItem[] => {
      switch (ref.kind) {
        case 'mail': {
          const t = mailById.get(ref.id);
          return t ? [{ kind: 'mail', t, onStart: () => onDraft(draftReplyWords(t), { kind: 'mail', id: t.thread_id }) }] : [];
        }
        case 'text': {
          const t = textById.get(ref.id);
          return t ? [{ kind: 'text', t }] : [];
        }
        case 'call': {
          const c = callById.get(ref.id);
          return c ? [{ kind: 'call', c }] : [];
        }
        case 'todo': {
          const todo = todoById.get(ref.id);
          if (!todo) return [];
          // The waiting email from its contact - only while it is on his list, as on the task's own card.
          const mail = todo.reply_thread_id ? mailById.get(todo.reply_thread_id) ?? null : null;
          return [{
            kind: 'todo', todo, mail,
            onStart: () => mail && onDraft(draftReplyWords(mail), { kind: 'mail', id: mail.thread_id, todo_task_id: todo.ghl_task_id }),
          }];
        }
        default:
          return [];
      }
    });
    // ⭐ Hide = "Not needed" for each item phase 1 can dismiss, one call each. A task is not dismissible there: it stays, and
    //   (with the others gone, a group of one is no group) shows alone. Nothing to hide (tasks only) = no Hide button.
    const dismissible = refs.filter(isDismissible);
    const groupHide: Hide | null = dismissible.length === 0 ? null : {
      error: dismissible.map((r) => hideErrors[refKey(r)]).find(Boolean) ?? null,
      onHide: () => dismissible.forEach((r) => hide(r.kind, r.id).onHide()),
    };
    return (
      <li key={`group:${cardKeyOf(refs)}`}>
        <GroupCard items={items} ai={aiFor(refs)} hide={groupHide} answering={answering} epoch={epoch} />
      </li>
    );
  };

  // Needs you: deadlines more than 30 days overdue go behind one tap, as on today's page.
  const oldIds = new Set(splitDeadlines(
    chat.groups.needs_you.filter((r) => r.kind === 'deadline').map((r) => deadlineById.get(r.id)).filter((d): d is MorningDeadline => !!d),
    new Date(),
  ).older.map((d) => d.deadline_id));
  const isOld = (r: MorningRef) => r.kind === 'deadline' && oldIds.has(r.id);
  const needsRefs = chat.groups.needs_you.filter((r) => !isOld(r) && hasCard(r));
  const olderRefs = chat.groups.needs_you.filter((r) => isOld(r) && hasCard(r));
  const waitRefs = chat.groups.can_wait.filter(hasCard);

  // MORNING2-AI Task 8: the groups as they stand NOW - an item with no row to draw (hidden, or gone from the sections) is out
  // of its group, and a group left with fewer than two is no group (its item is an ordinary card). ⛔ An item is claimed by
  // ONE group, so it can never be drawn twice.
  const claimed = new Set<string>();
  const groupOf = new Map<string, Unit>();
  for (const group of chat.contact_groups ?? []) {
    const members = group.filter((r) => hasCard(r) && !claimed.has(refKey(r)));
    if (members.length < 2) continue;
    for (const r of members) {
      claimed.add(refKey(r));
      groupOf.set(refKey(r), members);
    }
  }
  // The cards of each list, in order: an item alone, or its group ONCE at its first item. ⭐ `placed` is shared down the three
  // lists in the order Needs you, Older, Can wait: a group with an item in Needs you is ONE card there, and Can wait no
  // longer lists its other items; a group wholly in Can wait waits there, folded like any card.
  const placed = new Set<Unit>();
  const unitsOf = (refs: MorningRef[]): Unit[] => refs.flatMap((ref): Unit[] => {
    const group = groupOf.get(refKey(ref));
    if (!group) return [[ref]];
    if (placed.has(group)) return [];
    placed.add(group);
    return [group];
  });
  const needsUnits = unitsOf(needsRefs);
  const olderUnits = unitsOf(olderRefs);
  const waitUnits = unitsOf(waitRefs);

  // MORNING2-AI: the AI text for the cards SHOWN, top of the list first - one card per item, and a group's card is ONE entry
  // holding all its items. Folded lists wait until he opens them (a closed list costs nothing). The hook asks once per card
  // per read, so a Hide asks nothing, and a new read (`textRead`) asks again, because the same thread may have a newer message.
  const ai = useCardText(
    [...needsUnits, ...(showOld ? olderUnits : []), ...(showCanWait ? waitUnits : [])],
    textRead,
  );
  function aiFor(refs: Unit): CardText | undefined {
    const result = ai.texts[cardKeyOf(refs)];
    return result?.status === 'ready' ? result.text : undefined;
  }

  const cardForUnit = (refs: Unit): ReactNode => (refs.length > 1 ? groupCardFor(refs) : cardFor(refs[0]));
  const needs = needsUnits.map(cardForUnit).filter(Boolean);
  const older = olderUnits.map(cardForUnit).filter(Boolean);
  const canWait = waitUnits.map(cardForUnit).filter(Boolean);
  // Counts down as he hides them: what is still listed, plus what the lists did not show.
  const waitCount = canWait.length + chat.groups.can_wait_more;
  const waitLabel = `${waitCount}${chat.groups.can_wait_is_floor ? ' or more' : ''}`;

  // Where the rest of Can wait are - today's "and N more" links, each saying what it holds.
  const texts = data.texts.status === 'ok' ? data.texts : null;
  const rest: ReactNode[] = [];
  if (mail && (mail.more > 0 || mail.truncated)) {
    rest.push(
      <a key="mail" href={mail.inbox_url} target="_blank" rel="noopener noreferrer" className={LINK}>
        {mail.truncated ? 'and more emails - open Gmail' : `and ${plural(mail.more, 'more email', 'more emails')} - open Gmail`}
      </a>,
    );
  }
  if (texts && texts.more > 0) {
    rest.push(<Link key="texts" to="/crm/conversations" className={LINK}>{`and ${plural(texts.more, 'more text', 'more texts')}`}</Link>);
  }
  if (data.todosMore > 0) {
    rest.push(<Link key="todos" to="/todos" className={LINK}>{`and ${plural(data.todosMore, 'more task', 'more tasks')}`}</Link>);
  }

  return (
    <div className="min-w-0 space-y-6">
      {/* The greeting, as the assistant's own bubble - and (CHAT-DUET, 2026-10-28) the page's Refresh beside it as a round
          button, now that the page has no title row (it reads the list again, as the old Refresh did). */}
      <div className="flex min-w-0 items-start gap-2">
        <div className={`inline-block max-w-[85%] ${ASSISTANT_BUBBLE}`}>
          {chat.opening.map((words, i) => <p key={i} className={`break-words ${i ? 'mt-1' : ''}`}>{words}</p>)}
          {chat.opening.length > 1 && (
            <button type="button" onClick={onChanged} disabled={reading}
              className="mt-2 rounded-full border border-gray-300 bg-white px-3 py-1 text-[13px] font-medium text-gray-800 hover:border-gray-900 disabled:opacity-50">
              {reading ? 'Reading...' : 'Try again'}
            </button>
          )}
        </div>
        <button
          type="button"
          aria-label="Refresh"
          title="Refresh"
          onClick={onChanged}
          disabled={reading}
          className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-chat-ink-2 shadow-chat-float transition-colors hover:text-chat-ink disabled:opacity-60"
        >
          <RefreshIcon className={`h-5 w-5 ${reading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* The AI is paused: one line at the top, the cards below stay as they are (plain where there is no text). */}
      {ai.paused && (
        <p role="status" className="min-w-0 break-words text-[13px] leading-[18px] text-chat-ink-2">
          {ai.pausedReason === 'unrecorded' ? UNRECORDED_LINE : PAUSED_LINE}
        </p>
      )}

      {(needs.length > 0 || older.length > 0) && (
        <section aria-label="Needs you" className="min-w-0">
          {/* CHAT-DUET: the list speaks as a line from the assistant; the section keeps its name. */}
          <p className={`mb-3 ${LINE_BUBBLE}`}>These need you:</p>
          {needs.length > 0 && <ul className="space-y-3">{needs}</ul>}
          {older.length > 0 && (
            <>
              <button type="button" aria-expanded={showOld} onClick={() => setShowOld((v) => !v)} className={`${LINK} mt-3 inline-block`}>
                {`Older overdue deadlines (${older.length})`}
              </button>
              {showOld && <ul aria-label="Older overdue deadlines" className="mt-3 space-y-3">{older}</ul>}
            </>
          )}
        </section>
      )}

      {/* MORNING2-AI Task 14: below Needs you; it draws only for a switched-on person (this component is that block). */}
      <SuggestedForYou read={suggestRead} />

      {waitCount > 0 && (
        <section aria-label="Can wait" className="min-w-0">
          <p className={`mb-3 ${LINE_BUBBLE}`}>These can wait:</p>
          {/* ⭐ A control names its contents: a closed list that does not say what it holds reads as the items gone. */}
          <button type="button" aria-expanded={showCanWait} onClick={() => setShowCanWait((v) => !v)}
            className="rounded-full border border-gray-300 bg-white px-4 py-1.5 text-[13px] font-medium text-gray-800 hover:border-gray-900">
            {showCanWait ? 'Show less' : `Show the ${waitLabel}`}
          </button>
          {showCanWait && (
            <>
              {canWait.length > 0 && <ul className="mt-3 space-y-3">{canWait}</ul>}
              {rest.length > 0 && <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">{rest}</div>}
            </>
          )}
        </section>
      )}

      {data.radar.status === 'unreadable' ? (
        <Unreadable words={data.radar.words} onRetry={onChanged} busy={reading} />
      ) : data.radar.line && (
        <Link to="/deal-radar" className="block break-words rounded-[20px] bg-white px-4 py-3 text-[15px] font-medium text-chat-ink shadow-chat-card hover:bg-gray-50">
          {data.radar.line}
        </Link>
      )}
    </div>
  );
}
